import type {
  Account,
  IBusinessProfile,
  ILegacyAddress,
  IReceiptSettings,
  TReceiptSendMode,
} from '@feathr/blackbox';

/**
 * Structured address fields for iframe (mirrors ILegacyAddress but all optional for partial edits)
 * Uses camelCase for iframe communication
 */
export interface IOrganizationAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

/**
 * Organization data sent to the iframe for rendering receipt emails
 */
export interface IOrganization {
  name: string;
  logoUrl: string;
  address: IOrganizationAddress;
  taxId?: string;
  contactEmail?: string;
  contactPhone?: string;
}

/**
 * Golden-goose config format - nested structure used for both sending TO and receiving FROM iframe
 */
export interface IGoldenGooseConfig {
  branding?: {
    primaryColor?: string;
  };
  content?: {
    customMessage?: string;
    message?: string;
  };
  sender?: {
    fromName?: string;
    fromEmail?: string;
    sendMode?: TReceiptSendMode;
  };
  organization?: IOrganization;
}

/**
 * Build organization object for iframe init message from Account data
 */
export function buildOrganization(account: Account): IOrganization {
  const businessProfile = account.get('business_profile') as IBusinessProfile | undefined;
  const companyAddress = businessProfile?.company_address;

  return {
    name: account.get('name') || '',
    logoUrl: account.get('logo') || '',
    address: {
      line1: companyAddress?.line1 || undefined,
      line2: companyAddress?.line2 || undefined,
      city: companyAddress?.city || undefined,
      state: companyAddress?.state || undefined,
      postalCode: companyAddress?.postal_code || undefined,
      country: companyAddress?.country || undefined,
    },
    taxId: businessProfile?.tax_id || undefined,
    contactEmail: businessProfile?.contact_email || undefined,
    contactPhone: businessProfile?.contact_phone || undefined,
  };
}

/**
 * Convert iframe organization address back to ILegacyAddress for persistence
 */
export function toLegacyAddress(orgAddress: IOrganizationAddress): Partial<ILegacyAddress> {
  return {
    line1: orgAddress.line1,
    line2: orgAddress.line2,
    city: orgAddress.city,
    state: orgAddress.state,
    postal_code: orgAddress.postalCode,
    country: orgAddress.country,
  };
}

/**
 * Convert backend snake_case config to nested format for golden-goose (for sending TO iframe)
 */
export function toEditorConfig(config: IReceiptSettings, account: Account): IGoldenGooseConfig {
  return {
    branding: {
      primaryColor: config.primary_color,
    },
    content: {
      customMessage: config.custom_message,
      message: config.custom_message,
    },
    sender: {
      fromName: config.from_name,
      fromEmail: config.from_email,
      sendMode: config.send_mode,
    },
    organization: buildOrganization(account),
  };
}

/**
 * Result from parsing golden-goose config
 */
export interface IFromEditorResult {
  receiptSettings: IReceiptSettings;
  organization?: IOrganization;
}

/**
 * Convert golden-goose nested config to backend snake_case config (for receiving FROM iframe)
 */
export function fromEditorConfig(config: IGoldenGooseConfig): IFromEditorResult {
  return {
    receiptSettings: {
      primary_color: config.branding?.primaryColor,
      custom_message: config.content?.customMessage ?? config.content?.message,
      from_name: config.sender?.fromName,
      from_email: config.sender?.fromEmail,
      send_mode: config.sender?.sendMode,
    },
    organization: config.organization,
  };
}

const ENV_CONFIG: Record<string, string> = {
  'blackbox.feathr.co': 'https://forms.feathr.co',
  'blackbox-staging.feathr.co': 'https://forms-staging.feathr.co',
  'blackbox-qa.feathr.co': 'https://forms-qa.feathr.co',
  'blackbox-dev.feathr.co': 'https://forms-dev.feathr.co',
};

const LOCAL_CONFIG = {
  url: 'https://local.feathr.app:9009/',
  origin: 'https://local.feathr.app:9009',
};

export function getEmailConfiguratorUrl(): { url: string; origin: string } {
  if (globalThis.location.hostname.includes('local.feathr.app')) {
    return LOCAL_CONFIG;
  }

  for (const [blackboxHost, formsOrigin] of Object.entries(ENV_CONFIG)) {
    if (BLACKBOX_URL.includes(blackboxHost)) {
      return {
        url: `${formsOrigin}/email-configurator/index.html`,
        origin: formsOrigin,
      };
    }
  }

  return LOCAL_CONFIG;
}
